import { Injectable } from '@angular/core';


// Configures CKEditor 4 upload behavior app-wide. Called from AppComponent constructor
// so the listeners are installed before router-outlet activates any route and any
// editor can mount. CKEditor 4 loads synchronously from the CDN earlier in index.html
// so the CKEDITOR global is defined by the time Angular bootstraps.
//
// Only touches the CKEditor 4 global (window.CKEDITOR). CKEditor 5 uses a separate
// module-scoped API (ClassicEditor from @ckeditor/ckeditor5-*) and is unaffected.
//
// To reverse: delete this file, remove the import and DI from AppComponent constructor,
// and remove the call to configureCkeditor4().
@Injectable({ providedIn: 'root' })
export class CkEditorConfigService {

  configureCkeditor4(): void {
    try {
      const CKEDITOR = (window as any).CKEDITOR;
      if (!CKEDITOR || typeof CKEDITOR.on !== 'function') return;

      // ng2-ckeditor's ckeditor.component calls CKEDITOR.removeAllListeners() in its
      // ngOnDestroy (node_modules/ng2-ckeditor/esm2020/ckeditor.component.mjs:52).
      // This is overly aggressive - ng2-ckeditor only registers per-editor listeners
      // (via this.instance.on(...)) which are cleaned up by this.instance.destroy();
      // it does not need to wipe the CKEDITOR global. The global wipe destroys every
      // plugin's registered event handlers (filebrowser, uploadimage, our listeners,
      // etc.) so second-and-later editors mount without the plumbing that makes the
      // Image dialog Upload tab visible and its "Send to Server" button functional.
      //
      // Neutering the global removeAllListeners keeps every plugin's listeners in
      // place for the app's lifetime, which is what CKEditor plugins expect. If any
      // caller genuinely needs the original behavior, it is preserved at
      // CKEDITOR.__yhOriginalRemoveAllListeners.
      if (!(CKEDITOR as any).__yhOriginalRemoveAllListeners) {
        const originalRemoveAllListeners = CKEDITOR.removeAllListeners;
        if (typeof originalRemoveAllListeners === 'function') {
          (CKEDITOR as any).__yhOriginalRemoveAllListeners = originalRemoveAllListeners;
          CKEDITOR.removeAllListeners = function () { /* intentional no-op, see service comment */ };
        }
      }

      this._attachListeners(CKEDITOR);
    } catch (_e) {
      // Global setup failure - CKEditor probably not loaded. App still works without
      // image upload, matching pre-fix behavior.
    }
  }


  // Attaches the two global listeners that make image upload work end-to-end.
  // Idempotent: re-invoked after ng2-ckeditor wipes global listeners on component destroy.
  private _attachListeners(CKEDITOR: any): void {
    // Per-editor upload URLs so the uploadimage widget (drag/drop + paste) has an
    // endpoint. Fires early in the editor lifecycle before plugins initialize.
    // Redundant for html-editor.component (already sets these) but covers any other
    // CKEditor 4 instance that shows the Image button without setting the URLs itself.
    CKEDITOR.on('instanceCreated', (evt: any) => {
      try {
        if (evt?.editor?.config) {
          evt.editor.config.filebrowserImageUploadUrl = '/api/files/upload-ckeditor4?src=filebrowser';
          evt.editor.config.uploadUrl = '/api/files/upload-ckeditor4?src=uploadwidget';
        }
      } catch (_e) {
        // Per-editor failure - skip this editor, do not affect others.
      }
    });

    // Force the Image dialog's Upload page visible on every build. On second-and-later
    // editor instances (after ng2-ckeditor's removeAllListeners wipe + re-attach cycle),
    // CKEditor 4's filebrowser plugin leaves the Upload page with hidden=true even when
    // the upload URLs are set on the editor config - which renders the tab with
    // display:none. Flipping it here happens during dialog build so the resulting DOM
    // has the tab visible from the start. Scoped strictly to the image dialog so no
    // other dialog (Link, Table, etc.) is touched.
    CKEDITOR.on('dialogDefinition', (evt: any) => {
      try {
        if (evt?.data?.name !== 'image') return;
        const dialogDefinition = evt.data.definition;
        if (!dialogDefinition || typeof dialogDefinition.getContents !== 'function') return;

        const existingUpload = dialogDefinition.getContents('Upload');
        if (existingUpload) {
          if (existingUpload.hidden === true) existingUpload.hidden = false;
          return;
        }

        // Fallback: no filebrowser plugin present, add the tab ourselves so drag/drop
        // upload is not the only path to attach an image.
        const editor = evt.editor;
        const lang = editor?.lang?.image || {};

        dialogDefinition.addContents({
          id: 'Upload',
          hidden: false,
          filebrowser: 'uploadButton',
          label: lang.upload || 'Upload',
          elements: [
            {
              type: 'file',
              id: 'upload',
              label: lang.btnUpload || 'Send it to the Server',
              style: 'height:40px',
              size: 38
            },
            {
              type: 'fileButton',
              id: 'uploadButton',
              filebrowser: 'info:txtUrl',
              label: lang.btnUpload || 'Send it to the Server',
              'for': ['Upload', 'upload']
            }
          ]
        });
      } catch (_e) {
        // Per-dialog failure - skip. Other dialogs and other editors unaffected.
      }
    });
  }
}
